/**
 * ContractSlice
 * 
 * Redux slice for managing contract state.
 * 
 * This slice follows the modular pattern with:
 * - state.ts: State interface and initial state
 * - selectors.ts: Memoized selectors
 * - extraReducers.ts: API endpoint matchers
 * - reducers/: Split reducer files by domain
 */

import { createSlice } from '@reduxjs/toolkit';
import { initialState, ContractSliceState } from './state';
import { buildContractExtraReducers } from './extraReducers';

// Import all reducers (renamed to avoid conflicts with exported actions)
import {
  setSelectedContract as setSelectedContractReducer,
  setSelectedFeeSchedule as setSelectedFeeScheduleReducer,
  setIsEditing as setIsEditingReducer,
  setIsDirty as setIsDirtyReducer,
  updateContract as updateContractReducer,
  updateFeeSchedule as updateFeeScheduleReducer,
  clearContract as clearContractReducer,
  initializeContract as initializeContractReducer,
  setSelectedContractTabIndex as setSelectedContractTabIndexReducer,
  addCenterToContract as addCenterToContractReducer,
  removeCenterFromContract as removeCenterFromContractReducer,
  updateContractCenters as updateContractCentersReducer,
  openRuleModal as openRuleModalReducer,
  closeRuleModal as closeRuleModalReducer,
  setSelectedRuleId as setSelectedRuleIdReducer,
  toggleRuleModal as toggleRuleModalReducer,
  setValidationError as setValidationErrorReducer,
  setValidationErrors as setValidationErrorsReducer,
  clearValidationError as clearValidationErrorReducer,
  clearAllValidationErrors as clearAllValidationErrorsReducer,
} from './reducers';

/**
 * Contract slice definition
 */
const contractSlice = createSlice({
  name: 'contract',
  initialState,
  reducers: {
    // Core reducers
    setSelectedContract: setSelectedContractReducer,
    setSelectedFeeSchedule: setSelectedFeeScheduleReducer,
    setIsEditing: setIsEditingReducer,
    setIsDirty: setIsDirtyReducer,
    updateSelectedContract: updateContractReducer,
    updateSelectedFeeSchedule: updateFeeScheduleReducer,
    clearSelectedContract: clearContractReducer,
    initializeContract: initializeContractReducer,
    setSelectedContractTabIndex: setSelectedContractTabIndexReducer,
    addCenterToContract: addCenterToContractReducer,
    removeCenterFromContract: removeCenterFromContractReducer,
    updateContractCenters: updateContractCentersReducer,

    // Rule modal reducers
    openRuleModal: openRuleModalReducer,
    closeRuleModal: closeRuleModalReducer,
    setSelectedRuleId: setSelectedRuleIdReducer,
    toggleRuleModal: toggleRuleModalReducer,

    // Validation reducers
    setValidationError: setValidationErrorReducer,
    setValidationErrors: setValidationErrorsReducer,
    clearValidationError: clearValidationErrorReducer,
    clearAllValidationErrors: clearAllValidationErrorsReducer,
  },
  extraReducers: buildContractExtraReducers,
});

// Export actions
export const contractActions = contractSlice.actions;

// Export individual actions with Action suffix for convenience
export const {
  setSelectedContract: setSelectedContractAction,
  setSelectedFeeSchedule: setSelectedFeeScheduleAction,
  setIsEditing: setIsEditingAction,
  setIsDirty: setIsDirtyAction,
  updateSelectedContract: updateSelectedContractAction,
  updateSelectedFeeSchedule: updateSelectedFeeScheduleAction,
  clearSelectedContract: clearSelectedContractAction,
  initializeContract: initializeContractAction,
  setSelectedContractTabIndex: setSelectedContractTabIndexAction,
  addCenterToContract: addCenterToContractAction,
  removeCenterFromContract: removeCenterFromContractAction,
  updateContractCenters: updateContractCentersAction,
  openRuleModal: openRuleModalAction,
  closeRuleModal: closeRuleModalAction,
  setSelectedRuleId: setSelectedRuleIdAction,
  toggleRuleModal: toggleRuleModalAction,
  setValidationError: setValidationErrorAction,
  setValidationErrors: setValidationErrorsAction,
  clearValidationError: clearValidationErrorAction,
  clearAllValidationErrors: clearAllValidationErrorsAction,
} = contractSlice.actions;

// Also export with original names for backward compatibility
export const {
  setSelectedContract,
  setSelectedFeeSchedule,
  setIsEditing,
  setIsDirty,
  updateSelectedContract,
  updateSelectedFeeSchedule,
  clearSelectedContract,
  initializeContract,
  setSelectedContractTabIndex,
  addCenterToContract,
  removeCenterFromContract,
  updateContractCenters,
  openRuleModal,
  closeRuleModal,
  setSelectedRuleId,
  toggleRuleModal,
  setValidationErrors,
  clearValidationError,
  clearAllValidationErrors,
} = contractSlice.actions;

// Export reducer
export default contractSlice.reducer;

// Re-export state types
export type { ContractSliceState } from './state';

// Re-export selectors
export * from './selectors';
